import { useId, useState, type ReactNode } from "react";

const KEY = "bd.admin.fold.";

/** What this admin left open, for as long as the page stays in this browser tab. */
function recall(id: string): boolean {
  try {
    return sessionStorage.getItem(KEY + id) === "1";
  } catch {
    return false; // private mode, storage off: every fold starts closed
  }
}

function remember(id: string, open: boolean): void {
  try {
    sessionStorage.setItem(KEY + id, open ? "1" : "0");
  } catch {
    // nothing to do: it is only a convenience
  }
}

/**
 * A part of a tab that stays shut until it is wanted: one full-width key with the title, what is
 * inside in a few words, and a plus or a minus. It opens in place (nothing scrolls), starts
 * closed, and with an `id` is remembered for the session. Give `open` and `onToggle` instead and
 * whoever holds it decides.
 */
export function Fold({ id, title, summary, warn, open: held, onToggle, children }: {
  id?: string;
  title: ReactNode;
  summary?: ReactNode;
  /** The closed line is asking for something: it is drawn in the warning colour. */
  warn?: boolean;
  open?: boolean;
  onToggle?: (open: boolean) => void;
  children: ReactNode;
}) {
  const [mine, setMine] = useState(() => (id ? recall(id) : false));
  const open = held ?? mine;
  const body = useId();

  function toggle() {
    const next = !open;
    if (held === undefined) {
      setMine(next);
      if (id) remember(id, next);
    }
    onToggle?.(next);
  }

  return (
    <section className={"ad-fold" + (open ? " open" : "") + (warn ? " warn" : "")}>
      <button type="button" className="ad-fold-head" aria-expanded={open} aria-controls={open ? body : undefined} onClick={toggle}>
        <span className="ad-fold-text">
          <span className="ad-fold-title">{title}</span>
          {summary && <span className="ad-fold-sum">{summary}</span>}
        </span>
        <span className="ad-fold-sign" aria-hidden="true">{open ? "-" : "+"}</span>
      </button>
      {open && <div id={body} className="ad-card ad-fold-body">{children}</div>}
    </section>
  );
}
